import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import { catchError, switchMap } from 'rxjs';
import { throwError } from 'rxjs';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { isIeCall } from '@aeCore/helpers/interceptor.helper';
import { AuthService } from '@aeCore/services/auth.service';
import { TokenService } from '@aeCore/services/token.service';
import { getErrorKey } from '@aeCore/helpers/error.helper';
import { ErrorKeys } from '@aeCore/enums/errors.enum';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isIeCall(req)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const tokenService = inject(TokenService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        switch (error.status) {
          case HttpStatusCode.Unauthorized: {
            const errKey = getErrorKey(error);
            if (errKey === ErrorKeys.INVALID_TFA_TOKEN) {
              break;
            }

            // If access token expired, attempt refresh and retry the request
            if (errKey === 'ACCESS_TOKEN_EXPIRED') {
              if (!tokenService.getRefreshToken()) {
                if (authService.isAuthenticated()) {
                  authService.removeAuthData();
                  router.navigate(['auth']);
                }
                break;
              }

              return tokenService.refreshToken().pipe(
                switchMap((tokens) => {
                  const newReq = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${tokens.accessToken}`,
                    },
                  });
                  return next(newReq);
                }),
                catchError(() => {
                  if (authService.isAuthenticated()) {
                    authService.removeAuthData();
                    router.navigate(['auth']);
                  }
                  return throwError(() => error);
                }),
              );
            }

            if (authService.isAuthenticated()) {
              authService.removeAuthData();
              router.navigate(['auth']);
            }
            break;
          }
          case HttpStatusCode.Forbidden: {
            console.log(error.error);
            if (getErrorKey(error) === ErrorKeys.TFA_ACTIVATION_REQUIRED) {
              router.navigate(['auth', 'tfa-setup'], {
                queryParams: { state: 'generate' },
              });
            } else if (getErrorKey(error) === ErrorKeys.TFA_NOT_VERIFIED) {
              router.navigate(['auth', 'tfa-setup']);
            }
            break;
          }
          case HttpStatusCode.PreconditionFailed: {
            if (authService.isAuthenticated()) {
              router.navigate(['auth', 'set-new-password']);
            }
            break;
          }
        }
      }
      return throwError(error);
    }),
  );
};
